/**
 * Informations de l'ÉMETTEUR (Nexxus) affichées dans les documents du poste (CGU, consentement Hybride,
 * politique de collecte, pieds de page, aide). Source unique : l'écran « Informations de l'émetteur » de la
 * console d'administration, relayé par le serveur de licences (copie SIGNÉE, mise en cache sur le poste pour
 * fonctionner hors ligne). Aucune coordonnée d'émetteur n'est écrite en dur dans l'application : une valeur
 * absente donne une formulation neutre, jamais un passage entre crochets ni une valeur inventée.
 */
export const ISSUER_INFO_FIELDS = [
  'legal_name',
  'legal_form',
  'share_capital',
  'nif',
  'rccm',
  'head_office',
  'address',
  'phone',
  'contact_email',
  'website',
  'publication_director',
  'host_name',
  'host_address',
  'tax_mention',
  'privacy_contact_email',
  'subprocessors',
  'restore_months',
  'deletion_days',
] as const;

export type IssuerInfoField = (typeof ISSUER_INFO_FIELDS)[number];
export type IssuerInfo = Record<IssuerInfoField, string>;

export const EMPTY_ISSUER_INFO: IssuerInfo = Object.freeze(
  Object.fromEntries(ISSUER_INFO_FIELDS.map((f) => [f, ''])),
) as IssuerInfo;

/** Marque produit utilisée tant que la raison sociale n'est pas connue (ce n'est pas une donnée d'émetteur). */
export const PRODUCT_BRAND = 'Nexxus';

export function parseIssuerInfo(raw: unknown): IssuerInfo {
  const out: Record<string, string> = { ...EMPTY_ISSUER_INFO };
  if (raw && typeof raw === 'object') {
    const record = raw as Record<string, unknown>;
    for (const field of ISSUER_INFO_FIELDS) {
      const value = record[field];
      if (typeof value === 'string') out[field] = value.trim();
    }
  }
  return out as IssuerInfo;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function oneLine(value: string): string {
  return value.replace(/\s*[\r\n]+\s*/g, ', ').trim();
}

/** Nom affiché : raison sociale, sinon la marque produit. */
export function issuerName(info: IssuerInfo): string {
  return info.legal_name || PRODUCT_BRAND;
}

/** « Nexxus SAS, sise à … » : éditeur du logiciel, avec ce qui est connu seulement. */
export function issuerEditorPhrase(info: IssuerInfo): string {
  const identity = [info.legal_name || PRODUCT_BRAND, info.legal_form]
    .filter((x) => x !== '')
    .join(' ');
  return info.head_office ? `${identity}, sise à ${oneLine(info.head_office)}` : identity;
}

/** Adresse de contact générale (support, droits) ; vide si inconnue. */
export function issuerContactEmail(info: IssuerInfo): string {
  return info.contact_email;
}

/** Adresse pour exercer les droits sur les données personnelles. */
export function issuerPrivacyEmail(info: IssuerInfo): string {
  return info.privacy_contact_email || info.contact_email;
}

/** « contactez X (a@b) » ou « contactez X » sans adresse inventée. */
export function issuerContactPhrase(info: IssuerInfo): string {
  const email = issuerContactEmail(info);
  return email ? `${issuerName(info)} (${email})` : issuerName(info);
}

export function issuerFooterLine(info: IssuerInfo, year: number, version: string): string {
  return `© ${String(year)} ${issuerName(info)} · Version ${version}`;
}

/** Sous-titre des CGU. */
export function cguSubtitle(info: IssuerInfo, version: string, period: string): string {
  const where = info.head_office ? `, ${oneLine(info.head_office)}` : '';
  return `Version ${version} — ${period} | ${[info.legal_name || PRODUCT_BRAND, info.legal_form].filter((x) => x !== '').join(' ')}${where}`;
}

/** Durée de restauration après expiration (mois) si connue. */
export function restoreMonths(info: IssuerInfo): number | null {
  const n = Number.parseInt(info.restore_months, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function deletionDays(info: IssuerInfo): number | null {
  const n = Number.parseInt(info.deletion_days, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Les trois passages « pratiques » du consentement Hybride (durée, hébergement, contact). */
export function consentPracticalPoints(info: IssuerInfo): Array<{ title: string; text: string }> {
  const months = restoreMonths(info);
  const days = deletionDays(info);
  const retention =
    months !== null && days !== null
      ? `Pendant la durée de la licence ; après expiration, restauration possible pendant ${String(months)} mois, puis suppression définitive des données sous ${String(days)} jours.`
      : `Pendant la durée de la licence. Les délais de restauration et de suppression définitive après expiration vous sont communiqués par ${issuerName(info)}.`;

  const hosting = info.host_name
    ? `Hébergement : ${info.host_name}${info.host_address ? ` (${oneLine(info.host_address)})` : ''}.`
    : 'Hébergement : information non disponible sur ce poste (connectez-vous à Internet pour la récupérer).';
  const processors = info.subprocessors
    ? `Sous-traitants ultérieurs : ${oneLine(info.subprocessors)}.`
    : 'Les sous-traitants ultérieurs éventuels (envoi d’e-mails, stockage de fichiers) sont listés dans l’annexe au contrat.';

  const privacy = issuerPrivacyEmail(info);
  const contact = privacy
    ? `Pour exercer vos droits : ${privacy}.`
    : `Pour exercer vos droits, contactez ${issuerName(info)}.`;

  return [
    { title: 'Durée de conservation', text: retention },
    { title: 'Hébergement', text: `${hosting} ${processors}` },
    { title: 'Contact', text: contact },
  ];
}

/** Texte du consentement Hybride (écran de consentement simple). */
export function buildHybridConsentText(info: IssuerInfo): string {
  const months = restoreMonths(info);
  const restore =
    months !== null
      ? `Après expiration de la licence, la restauration reste possible pendant ${String(months)} mois.`
      : 'Après expiration de la licence, la restauration reste possible pendant le délai communiqué par l’éditeur.';
  return `Votre licence Hybride synchronise votre dossier chez ${issuerName(info)} dès que le réseau revient, et envoie chaque soir une copie de sécurité chiffrée de bout en bout (e-mail de confirmation).

Nexxus ne peut jamais lire le contenu de ces copies : seules votre phrase secrète ou votre clé de récupération permettent de les ouvrir.

Cette copie comprend les fiches métier (salariés, bulletins, écritures, etc.) et les pièces du coffre-fort. Nexxus conserve jusqu’à 30 sauvegardes, isolées par entreprise. ${restore}

Le travail quotidien reste possible hors connexion sur votre poste. La direction peut consulter le dossier en lecture sur le web.

Sans cet accord, la synchronisation automatique et la copie du soir ne partent pas.`;
}
